import { z } from "zod";
import { IdSchema } from "./primitives.js";
/** The longest wording a test name stores, the same limit as a rule's ordered-test wording. */
export const FORM_TEST_NAME_MAX_LENGTH = 240;
const TestWordingSchema = z
    .string()
    .trim()
    .min(1)
    // Punctuation alone names no test and would match almost every ordered test.
    .regex(/[\p{L}\p{N}]/u, "Use letters or numbers.")
    // Counted in characters, as the database counts them, so every stored name reads back: a
    // character outside the Basic Multilingual Plane is one character but two UTF-16 units.
    .refine((value) => Array.from(value).length <= FORM_TEST_NAME_MAX_LENGTH, `Use at most ${FORM_TEST_NAME_MAX_LENGTH} characters.`);
/**
 * How the clinic writes an ordered test on its forms. An ordered test whose name contains
 * `orderedAs` as whole words counts, for form rules, as the test the forms call `writtenAs`: with
 * "RPR" written as "Syphilis", a rule for "Syphilis" applies to an RPR order and writes
 * "Syphilis". Only an administrator makes an entry active. A clinician's correction on a prepared
 * form is only ever a suggestion until an administrator approves it.
 */
export const FormTestNameSchema = z.object({
    id: IdSchema,
    orderedAs: TestWordingSchema,
    writtenAs: TestWordingSchema,
    status: z.enum(["active", "suggested", "dismissed"]),
    origin: z.enum(["admin", "clinician"]),
    /** How many prepared form sets clinicians corrected this way. Zero for an administrator's entry. */
    timesSeen: z.number().int().nonnegative(),
    updatedAt: z.string(),
});
/**
 * At most 500 names in use and 500 suggestions awaiting review: the database keeps each clinic
 * within both limits and lists no dismissed suggestion (migration 20260927220301_form_test_names).
 */
export const FormTestNamesSchema = z.array(FormTestNameSchema).max(1_000);
/** An administrator's change to the clinic's test names. */
export const FormTestNameWriteSchema = z.discriminatedUnion("operation", [
    z.strictObject({
        operation: z.literal("add"),
        orderedAs: TestWordingSchema,
        writtenAs: TestWordingSchema,
    }),
    z.strictObject({ operation: z.enum(["approve", "dismiss", "remove"]), id: IdSchema }),
]);
/** A clinician's hand correction on a prepared form, recorded for an administrator to review. */
export const FormTestNameSuggestionSchema = z.strictObject({
    orderedAs: TestWordingSchema,
    writtenAs: TestWordingSchema,
});
//# sourceMappingURL=form-test-names.js.map